// src/components/widgets/EmbedCodeModal.jsx
import React, { useEffect, useState } from 'react';
import {
  X,
  Copy,
  Check,
  Code,
  ExternalLink,
  BookOpen,
  Globe,
  Layout,
  MousePointerClick,
  MessageCircle
} from 'lucide-react';

// shadcn components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';

import { useWidgets } from '../../hooks/useWidgets';
import { cn } from '../../utils/cn';

const EmbedCodeModal = ({ widgetId, onClose }) => {
  const { generateEmbedCode, getEmbedCode, generatingEmbed } = useWidgets();
  const [copiedSection, setCopiedSection] = useState(null);

  // Load embed code when modal opens
  useEffect(() => {
    if (widgetId) {
      generateEmbedCode(widgetId);
    }
  }, [widgetId, generateEmbedCode]);

  // Get the embed code data
  const embedData = getEmbedCode(widgetId);

  // Handle copy to clipboard
  const handleCopy = async (text, section) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (!embedData && !generatingEmbed) {
    return null;
  }

  // Generate different embed code variations
  const generateEmbedVariations = () => {
    if (!embedData) return {};

    const baseUrl = embedData.preview_url;
    const widgetName = embedData.widget_name || 'Booking Widget';

    return {
      // Inline widget - responsive iframe
      inline: `<!-- Inline Booking Widget -->
<div style="position: relative; width: 100%; height: 0; padding-bottom: 75%;">
  <iframe
    src="${baseUrl}"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;"
    allowfullscreen>
  </iframe>
</div>`,

      // Popup button - opens widget in modal
      popup: `<!-- Popup Booking Button -->
<a href="#" id="booking-widget-trigger" style="display: inline-block; padding: 12px 24px; background: #0066ff; color: white; text-decoration: none; border-radius: 6px; font-weight: 500;">
  Book Appointment
</a>

<div id="booking-widget-modal" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 9999; align-items: center; justify-content: center;">
  <div style="position: relative; width: 90%; max-width: 800px; height: 90%; max-height: 700px; background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.2);">
    <button id="booking-widget-close" style="position: absolute; top: 12px; right: 12px; width: 32px; height: 32px; border: none; background: #f3f4f6; border-radius: 50%; cursor: pointer; font-size: 20px; z-index: 10000;">×</button>
    <iframe src="${baseUrl}" style="width: 100%; height: 100%; border: none; border-radius: 12px;"></iframe>
  </div>
</div>

<script>
  (function() {
    var trigger = document.getElementById('booking-widget-trigger');
    var modal = document.getElementById('booking-widget-modal');
    var close = document.getElementById('booking-widget-close');

    trigger.addEventListener('click', function(e) {
      e.preventDefault();
      modal.style.display = 'flex';
    });

    close.addEventListener('click', function() {
      modal.style.display = 'none';
    });

    modal.addEventListener('click', function(e) {
      if (e.target === modal) {
        modal.style.display = 'none';
      }
    });
  })();
</script>`,

      // Floating badge - persistent button
      badge: `<!-- Floating Booking Badge -->
<div id="booking-widget-badge" style="position: fixed; bottom: 24px; right: 24px; z-index: 9998;">
  <button id="booking-badge-trigger" style="padding: 14px 20px; background: #0066ff; color: white; border: none; border-radius: 50px; font-weight: 500; cursor: pointer; box-shadow: 0 4px 12px rgba(0,102,255,0.4); display: flex; align-items: center; gap: 8px;">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
    Book Now
  </button>
</div>

<div id="booking-badge-modal" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 9999; align-items: center; justify-content: center;">
  <div style="position: relative; width: 90%; max-width: 800px; height: 90%; max-height: 700px; background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.2);">
    <button id="booking-badge-close" style="position: absolute; top: 12px; right: 12px; width: 32px; height: 32px; border: none; background: #f3f4f6; border-radius: 50%; cursor: pointer; font-size: 20px; z-index: 10000;">×</button>
    <iframe src="${baseUrl}" style="width: 100%; height: 100%; border: none; border-radius: 12px;"></iframe>
  </div>
</div>

<script>
  (function() {
    var trigger = document.getElementById('booking-badge-trigger');
    var modal = document.getElementById('booking-badge-modal');
    var close = document.getElementById('booking-badge-close');

    trigger.addEventListener('click', function() {
      modal.style.display = 'flex';
    });

    close.addEventListener('click', function() {
      modal.style.display = 'none';
    });

    modal.addEventListener('click', function(e) {
      if (e.target === modal) {
        modal.style.display = 'none';
      }
    });
  })();
</script>`
    };
  };

  const embedVariations = generateEmbedVariations();

  const platformInstructions = [
    {
      platform: 'WordPress',
      icon: Globe,
      steps: [
        'Edit the page where you want to add the booking widget',
        'Add a new "Custom HTML" or "HTML" block',
        'Paste the iframe code into the HTML block',
        'Save and publish your page'
      ]
    },
    {
      platform: 'Squarespace',
      icon: Globe,
      steps: [
        'Edit the page in Squarespace',
        'Add a new "Code" block',
        'Paste the iframe code',
        'Save and publish'
      ]
    },
    {
      platform: 'Wix',
      icon: Globe,
      steps: [
        'Go to your Wix editor',
        'Click "+ Add" and select "Embed Code"',
        'Choose "HTML iframe"',
        'Paste the code and adjust size',
        'Save and publish'
      ]
    },
    {
      platform: 'Webflow',
      icon: Globe,
      steps: [
        'Drag an "Embed" element to your page',
        'Paste the iframe code',
        'Set width to 100% and height to 600px',
        'Publish your site'
      ]
    },
    {
      platform: 'Custom HTML',
      icon: Code,
      steps: [
        'Open your HTML file',
        'Find where you want the widget',
        'Paste the iframe code',
        'Save and upload to your server'
      ]
    }
  ];

  const isOpen = !!(widgetId && (embedData || generatingEmbed));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Embed Code</DialogTitle>
          <DialogDescription>
            {embedData ? `For "${embedData.widget_name}"` : 'Loading...'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {generatingEmbed ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Generating embed code...</p>
              </div>
            </div>
          ) : embedData ? (
            <Tabs defaultValue="iframe" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="iframe" className="flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  iframe Embed
                </TabsTrigger>
                <TabsTrigger value="instructions" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Instructions
                </TabsTrigger>
              </TabsList>

              <TabsContent value="iframe" className="space-y-6">
                {/* Embed Types - Three distinct options */}
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-1">Choose Your Embed Type</h3>
                    <p className="text-sm text-muted-foreground">
                      Pick the style that works best for your website. Each type is ready to copy and paste.
                    </p>
                  </div>

                  <div className="grid gap-4">
                    {/* 1. Inline Widget */}
                    <Card className="border-2">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                              <Layout className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <CardTitle className="text-base">Inline Widget</CardTitle>
                                <Badge variant="secondary" className="text-xs">Most Common</Badge>
                              </div>
                              <CardDescription className="text-sm">
                                Widget appears directly on your page, always visible. Best for dedicated booking pages.
                              </CardDescription>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopy(embedVariations.inline, 'inline')}
                            className="flex items-center gap-1 ml-2"
                          >
                            {copiedSection === 'inline' ? (
                              <>
                                <Check className="w-3 h-3" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                Copy
                              </>
                            )}
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={embedVariations.inline}
                          readOnly
                          className="font-mono text-xs resize-none h-20"
                        />
                      </CardContent>
                    </Card>

                    {/* 2. Popup Button */}
                    <Card className="border-2">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                              <MousePointerClick className="w-5 h-5" />
                            </div>
                            <div>
                              <CardTitle className="text-base">Popup Button</CardTitle>
                              <CardDescription className="text-sm">
                                Add a button or link that opens the widget in a modal. Best for CTAs, navigation, blog posts.
                              </CardDescription>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopy(embedVariations.popup, 'popup')}
                            className="flex items-center gap-1 ml-2"
                          >
                            {copiedSection === 'popup' ? (
                              <>
                                <Check className="w-3 h-3" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                Copy
                              </>
                            )}
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={embedVariations.popup}
                          readOnly
                          className="font-mono text-xs resize-none h-32"
                        />
                      </CardContent>
                    </Card>

                    {/* 3. Floating Badge */}
                    <Card className="border-2">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                              <MessageCircle className="w-5 h-5" />
                            </div>
                            <div>
                              <CardTitle className="text-base">Floating Badge</CardTitle>
                              <CardDescription className="text-sm">
                                Persistent floating button (bottom-right corner). Best for site-wide availability without being intrusive.
                              </CardDescription>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopy(embedVariations.badge, 'badge')}
                            className="flex items-center gap-1 ml-2"
                          >
                            {copiedSection === 'badge' ? (
                              <>
                                <Check className="w-3 h-3" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                Copy
                              </>
                            )}
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={embedVariations.badge}
                          readOnly
                          className="font-mono text-xs resize-none h-32"
                        />
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Preview Link */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Test Your Widget</h4>
                        <p className="text-sm text-muted-foreground">
                          Preview how your widget will look before embedding
                        </p>
                      </div>
                      <Button variant="outline" asChild>
                        <a
                          href={embedData.preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open Preview
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="instructions" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>How to Add to Your Website</CardTitle>
                    <CardDescription>
                      Choose your platform below for step-by-step instructions
                    </CardDescription>
                  </CardHeader>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {platformInstructions.map((platform, index) => {
                    const Icon = platform.icon;

                    return (
                      <Card key={index}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Icon className="w-5 h-5" />
                            {platform.platform}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ol className="space-y-2">
                            {platform.steps.map((step, stepIndex) => (
                              <li key={stepIndex} className="text-sm flex">
                                <span className="text-primary font-medium mr-2 flex-shrink-0">
                                  {stepIndex + 1}.
                                </span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* General Tips */}
                <Alert>
                  <AlertDescription>
                    <h4 className="font-medium mb-2">💡 Tips for Best Results</h4>
                    <ul className="space-y-1 text-sm">
                      <li>• Place the widget on a page where customers expect to book appointments</li>
                      <li>• Make sure the widget has enough space (at least 600px height)</li>
                      <li>• Test the widget on different devices to ensure it works properly</li>
                      <li>• Consider adding explanatory text above the widget</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                {/* Support */}
                <Card>
                  <CardHeader>
                    <CardTitle>Need Help?</CardTitle>
                    <CardDescription>
                      Having trouble embedding your widget? We're here to help!
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Button variant="link" className="p-0 h-auto">
                        View Documentation
                      </Button>
                      <span className="text-muted-foreground">•</span>
                      <Button variant="link" className="p-0 h-auto">
                        Contact Support
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : null}
        </div>

        {embedData && (
          <div className="flex-shrink-0 flex items-center justify-between pt-4 border-t mt-4">
            <div className="text-xs text-muted-foreground">
              Widget URL: <span className="font-mono">{embedData.preview_url}</span>
            </div>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmbedCodeModal;